// SEQ-180 extraction note (8 September 2026): this is the pure engine that
// used to live at src/services/pnlCalculationService.ts, moved here so it can
// be Deno-tested (see pnlAnalytics.test.ts) without pulling in React or
// Supabase at type-check time. The service file itself already had no
// runtime dependency on either: it only carried `import type { PnlCategory }
// from '../hooks/usePnlCategories'`, which TypeScript erases at compile time.
// That is why `deno run` on the service file alone worked with zero errors.
// The problem is transitive: `deno test`/`deno check` still resolve that type's
// declaration by loading the whole of usePnlCategories.ts, which imports
// `useToast` (ToastModal.tsx, which references `document`) and `supabase`
// (which references `import.meta.env`), neither available in Deno's default
// lib. Declaring an equivalent `PnlCategory` shape locally below breaks that
// link. TypeScript's structural typing means every existing caller (the real
// `PnlCategory` from usePnlCategories.ts, used in usePnlMonthlyData.ts and
// useDashboardPnl.ts) still satisfies this type with zero behaviour change.
// pnlCalculationService.ts now does `export * from '../lib/pnl/pnlCalculationCore'`
// so the public API at that import path is byte-identical to before.

import { PNL_TAB_TOTAL_GRAIN, wagesMultiplierAppliesAtGrain } from './wagesMultiplierGrain';

/** Structurally identical to src/hooks/usePnlCategories.ts's PnlCategory. */
export interface PnlCategory {
  name: string;
  is_profit_deduction: boolean;
  group: 'Wages' | 'Materials' | 'Other';
  /**
   * SEQ-182's "Include in Financial P&L" flag, per category. Undefined means
   * true: every category row written before this field existed is a live
   * dollar line whose meaning is already settled, so absence can never be
   * read as exclusion.
   *
   * Deliberately the same NAME as pnl_metric_registry.financial_inclusion
   * (20260909000100_pnl_typed_metrics.sql), because it is the same rule at
   * the other enforcement point. That column governs the analytics RPC;
   * this field governs the V1 engine below, which is what the project P&L
   * tab and the company dashboard both read.
   *
   * The registry column defaults to FALSE and this one defaults to true.
   * That is not an inconsistency: a newly seeded registry key has no
   * history to preserve and should fail closed, whereas an entry already in
   * app_settings.pnl_expense_categories has fifteen months of totals
   * resting on it.
   */
  financial_inclusion?: boolean;
}

/**
 * Whether a configured category's values belong in the financial P&L at all.
 *
 * The one place the default is decided, so the tab, the dashboard, the
 * category editor and the tests cannot each answer it differently.
 */
export function categoryEntersFinancialPnl(category: PnlCategory): boolean {
  return category.financial_inclusion !== false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PnlSettings {
  materialPercent: number;   // e.g. 30  (meaning 30%)
  wagesMultiplier: number;   // e.g. 1.04 (meaning 104%)
  estimateProfit: number;    // target profit dollar amount
}

export interface MonthlyPnlResult {
  constructionIncome: number;
  retention: number;
  materialsBudget: number;
  expensesByCategory: Record<string, number>;
  wagesSubtotal: number;
  wagesWithMultiplier: number;
  otherExpensesTotal: number;
  totalDeductions: number;
  netProfit: number;
  profitPercent: number;
  wagesPercent: number;
  materialsPercent: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core calculation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculate P&L for a single month.
 *
 * Formulas (matching Excel):
 *   Materials Budget  = Construction Income × (materialPercent / 100)
 *   Materials Actual  = Σ expenses where category.group === 'Materials' && is_profit_deduction
 *   Wages Raw         = Σ 'Wages' & 'Extra wages' category amounts (pre-multiplier)
 *   Wages Multiplied  = Wages Raw × wagesMultiplier
 *   Other Labor       = Σ other Wages-group categories (Contractors, Stripping, etc.)
 *   Wages Total       = Wages Multiplied + Other Labor
 *   Other Expenses    = Σ expenses in 'Other' group with is_profit_deduction
 *   Total Deductions  = Materials Budget + Materials Actual + Wages Total + Other Expenses
 *   Net Profit        = Construction Income - Total Deductions
 *
 *   Profit %    = Net Profit / Construction Income           (Excel: =B32/B11)
 *   Wages %     = Wages Total / Construction Income          (Excel: =(B22+B23+B24+B26+B28)/B11)
 *   Materials % = (Materials Budget + Materials Actual) / CI  (Excel: =(B14+B18+B19+B20+B15)/B11)
 */
/*
 * WAGES MULTIPLIER, SINGLE-MONTH HALF (SEQ-180 follow-up, 14 September 2026).
 * This function deliberately applies NO wages multiplier: a month column shows
 * the typed value. That is the PNL_TAB_MONTH_GRAIN half of the one rule in
 * wagesMultiplierGrain.ts, which pnl_analytics_query and computePnlBuckets
 * also read, so an analytics month bucket now shows the same number this does.
 * There is no multiplication site here to route through the rule, so the
 * binding is proven behaviourally instead: wagesMultiplierGrain.test.ts asserts
 * this function's output is unchanged across three different multipliers.
 * `settings.wagesMultiplier` is consequently unused below, on purpose.
 */
export function calculateMonthlyPnl(
  constructionIncome: number,
  retention: number,
  manualExpenses: Record<string, number>,
  categories: PnlCategory[],
  settings: PnlSettings,
): MonthlyPnlResult {
  const materialsBudget = constructionIncome * (settings.materialPercent / 100);

  // Build a quick lookup for category metadata
  const categoryMap = new Map<string, PnlCategory>();
  categories.forEach(c => categoryMap.set(c.name, c));

  // Categories that get the wages multiplier
  let wagesTotal = 0;           // All wages-group items (values stored are already final)
  let materialsActualTotal = 0;  // Engineers, Handrails, Misc etc.
  let otherExpensesTotal = 0;    // Core costs, Patching, etc.
  const expensesByCategory: Record<string, number> = {};

  for (const [categoryName, amount] of Object.entries(manualExpenses)) {
    const cat = categoryMap.get(categoryName);
    const numAmount = Number(amount) || 0;
    expensesByCategory[categoryName] = numAmount;

    if (!cat) {
      // Unknown category: treat as deductible "Other".
      //
      // DELIBERATELY UNCHANGED, 14 September 2026. This is where a value
      // whose category nobody configured still gets subtracted from profit,
      // and it is the reason the two orphaned Aire - Newstead rows of
      // 8 September 2026 kept counting on the company dashboard (which sums
      // every manual row) after their categories were removed. It was
      // examined as part of that fix and left alone: it is plan decision D4,
      // the same rule pnl_metric_registry encodes as
      // expense.unmapped.is_deductible = true, and pnlAnalytics.test.ts
      // asserts both engines agree on it against a fixture written for that
      // purpose ("Unknown Category", D4). Changing it here alone would make
      // the analytics page and the project P&L tab disagree about the same
      // rows, which is worse than either answer.
      //
      // What changed instead is that an orphan can no longer be created
      // through the editor (features/pnlAnalytics/categoryRemoval.ts and
      // categoryRenameValues.ts) and one that already exists is named on the
      // analytics admin section rather than being invisible
      // (20260914000600_pnl_orphan_category_values.sql). Whether D4 itself
      // should be reversed is a separate decision with three code sites and
      // one migration behind it, written up for that decision to be taken.
      otherExpensesTotal += numAmount;
      continue;
    }

    if (!categoryEntersFinancialPnl(cat)) {
      // SEQ-182: shown on the P&L, never part of any total, ratio or net
      // profit. Recorded in expensesByCategory above for exactly that reason.
      continue;
    }

    if (cat.group === 'Wages') {
      // Values stored are already final amounts (multiplier already applied where relevant)
      wagesTotal += numAmount;
    } else if (cat.group === 'Materials') {
      if (cat.is_profit_deduction) {
        materialsActualTotal += numAmount;
      }
      // Materials with is_profit_deduction=false (the "Materials" row itself)
      // is tracked but not deducted: the budget replaces it
    } else {
      // "Other" group (Core costs, Patching, Yard, Office, etc.)
      if (cat.is_profit_deduction) {
        otherExpensesTotal += numAmount;
      }
    }
  }

  const wagesSubtotal = wagesTotal; // kept for interface compatibility
  const totalDeductions = materialsBudget + materialsActualTotal + wagesTotal + otherExpensesTotal;
  const netProfit = constructionIncome - totalDeductions;

  const profitPercent = constructionIncome > 0 ? (netProfit / constructionIncome) * 100 : 0;
  const wagesPercent = constructionIncome > 0 ? (wagesTotal / constructionIncome) * 100 : 0;
  const materialsPercent = constructionIncome > 0
    ? ((materialsBudget + materialsActualTotal) / constructionIncome) * 100
    : 0;

  return {
    constructionIncome,
    retention,
    materialsBudget,
    expensesByCategory,
    wagesSubtotal,
    wagesWithMultiplier: wagesTotal,
    otherExpensesTotal,
    totalDeductions,
    netProfit,
    profitPercent,
    wagesPercent,
    materialsPercent,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Aggregation: Calculate Totals across multiple months
// ─────────────────────────────────────────────────────────────────────────────

export function calculateTotalsPnl(
  monthlyResults: MonthlyPnlResult[],
  categories: PnlCategory[],
  settings: PnlSettings,
): MonthlyPnlResult {
  // Names whose aggregate total gets the wages multiplier (Excel applies ×1.04 to the SUM, not per-month)
  const MULTIPLIED_WAGES = new Set(['Wages', 'Extra wages']);
  const categoryMap = new Map<string, PnlCategory>();
  categories.forEach(c => categoryMap.set(c.name, c));

  const totals: MonthlyPnlResult = {
    constructionIncome: 0,
    retention: 0,
    materialsBudget: 0,
    expensesByCategory: {},
    wagesSubtotal: 0,
    wagesWithMultiplier: 0,
    otherExpensesTotal: 0,
    totalDeductions: 0,
    netProfit: 0,
    profitPercent: 0,
    wagesPercent: 0,
    materialsPercent: 0,
  };

  for (const month of monthlyResults) {
    totals.constructionIncome += month.constructionIncome;
    totals.retention += month.retention;
    totals.materialsBudget += month.materialsBudget;

    // Aggregate per-category expenses (raw sums)
    for (const [cat, amt] of Object.entries(month.expensesByCategory)) {
      totals.expensesByCategory[cat] = (totals.expensesByCategory[cat] || 0) + amt;
    }
  }

  // Now recalculate totals from aggregated expensesByCategory,
  // applying the wages multiplier only to Wages/Extra wages aggregates
  let wagesRawTotal = 0;   // Wages + Extra wages (will get ×multiplier)
  let otherLabor = 0;      // Contractors, Stripping, etc. (no multiplier)
  let materialsActualTotal = 0;
  let otherExpensesTotal = 0;

  for (const [categoryName, amount] of Object.entries(totals.expensesByCategory)) {
    const cat = categoryMap.get(categoryName);
    if (!cat) {
      // D4 again, kept in step with calculateMonthlyPnl above. See the long
      // note there for why this branch was examined and left alone.
      otherExpensesTotal += amount;
      continue;
    }

    // The Total column must exclude exactly what the month columns exclude,
    // or a project shows twelve months that do not add up to their own total.
    if (!categoryEntersFinancialPnl(cat)) continue;

    if (cat.group === 'Wages') {
      if (MULTIPLIED_WAGES.has(categoryName)) {
        wagesRawTotal += amount;
      } else {
        otherLabor += amount;
      }
    } else if (cat.group === 'Materials') {
      if (cat.is_profit_deduction) {
        materialsActualTotal += amount;
      }
    } else {
      if (cat.is_profit_deduction) {
        otherExpensesTotal += amount;
      }
    }
  }

  // Apply multiplier ONLY to Wages/Extra wages aggregate (Excel behavior).
  //
  // SEQ-180 follow-up (14 September 2026): WHETHER it applies is no longer
  // decided here. This function is the tab's Total column, i.e. one project
  // across the selected range, which is PNL_TAB_TOTAL_GRAIN; the shared rule in
  // wagesMultiplierGrain.ts says whether a bucket at that grain multiplies, and
  // pnl_analytics_query and computePnlBuckets read the same rule. Today it says
  // yes, so this is behaviour-identical to the plain multiplication it replaces.
  // Its value is that the tab and the analytics can no longer drift apart on
  // this question without one edit changing both.
  const effectiveMultiplier = wagesMultiplierAppliesAtGrain(PNL_TAB_TOTAL_GRAIN)
    ? settings.wagesMultiplier
    : 1;
  const wagesMultiplied = wagesRawTotal * effectiveMultiplier;
  totals.wagesSubtotal = wagesRawTotal + otherLabor;
  totals.wagesWithMultiplier = wagesMultiplied + otherLabor;
  totals.otherExpensesTotal = otherExpensesTotal;

  // Update the displayed Wages category total to show the multiplied value.
  // A Wages category excluded from the financial P&L is skipped: it never
  // entered wagesRawTotal above, so multiplying its displayed figure would
  // print a number that appears in no total on the page.
  for (const name of MULTIPLIED_WAGES) {
    const wagesCategory = categoryMap.get(name);
    if (wagesCategory && !categoryEntersFinancialPnl(wagesCategory)) continue;
    if (totals.expensesByCategory[name] !== undefined) {
      totals.expensesByCategory[name] = totals.expensesByCategory[name] * effectiveMultiplier;
    }
  }

  totals.totalDeductions = totals.materialsBudget + materialsActualTotal + totals.wagesWithMultiplier + otherExpensesTotal;
  totals.netProfit = totals.constructionIncome - totals.totalDeductions;

  // Recalculate percentages from totals
  const income = totals.constructionIncome;
  totals.profitPercent = income > 0 ? (totals.netProfit / income) * 100 : 0;
  totals.wagesPercent = income > 0 ? (totals.wagesWithMultiplier / income) * 100 : 0;
  totals.materialsPercent = income > 0 ? ((totals.materialsBudget + materialsActualTotal) / income) * 100 : 0;

  return totals;
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility: Generate month range
// ─────────────────────────────────────────────────────────────────────────────

export function getMonthRange(
  startMonth: number,
  startYear: number,
  endMonth: number,
  endYear: number,
): { month: number; year: number; key: string }[] {
  const months: { month: number; year: number; key: string }[] = [];
  let m = startMonth;
  let y = startYear;

  while (y < endYear || (y === endYear && m <= endMonth)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    months.push({ month: m, year: y, key });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return months;
}

/**
 * Convert a month/year pair to the `month_date` format used in the DB.
 * e.g. (3, 2025) → "2025-03-01"
 */
export function toMonthDate(month: number, year: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

/**
 * Convert a month_date string back to month/year.
 * e.g. "2025-03-01" → { month: 3, year: 2025 }
 */
export function fromMonthDate(monthDate: string): { month: number; year: number } {
  const parts = monthDate.split('-');
  return { year: parseInt(parts[0]), month: parseInt(parts[1]) };
}
